import { useEffect, useState, useCallback } from "react";
import { Loader2, Sparkles, MapPin, MessageSquareQuote, Check, Plus } from "lucide-react";
import { spaceApi } from "./spaceApi";
import { SpaceHomeBtn } from "./SpaceNav";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (path) => (path ? (path.startsWith("http") ? path : BACKEND + path) : null);

// Human-readable line for a worker's machine effect (matches the server buffs).
const effectLabel = (e = {}) => {
  if (e.yield_pct) return `+${Math.round(e.yield_pct * 100)}% Titanium production`;
  if (e.patrol_pct) return `+${Math.round(e.patrol_pct * 100)}% Road Patrol payout`;
  if (e.alloy_pct) return `+${Math.round(e.alloy_pct * 100)}% Micro-Alloy salvage`;
  return "Passive support";
};

// BaseStaff — the Sanctuary's non-combat worker NPCs. Assigning a worker (within
// the Command-Centre-gated slot cap) makes their base buff GO LIVE server-side:
// production yield + Road Patrol payouts are boosted for as long as they're on shift.
export default function BaseStaff({ onBack }) {
  const [workers, setWorkers] = useState(null);
  const [assigned, setAssigned] = useState([]);
  const [slots, setSlots] = useState(0);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  const loadState = useCallback(() => {
    spaceApi.getState()
      .then((s) => { setAssigned(s.assigned_staff || []); setSlots(s.staff_slots || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    spaceApi.baseStaff().then((d) => setWorkers(d.workers || [])).catch(() => setWorkers([]));
    loadState();
  }, [loadState]);

  const toggle = async (w) => {
    const on = assigned.includes(w.worker_id);
    setBusy(w.worker_id); setErr("");
    try {
      const s = on ? await spaceApi.unassignStaff(w.worker_id) : await spaceApi.assignStaff(w.worker_id);
      setAssigned(s.assigned_staff || []); setSlots(s.staff_slots || 0);
    } catch (e) {
      setErr(e.message || "Couldn't update staff");
    } finally { setBusy(null); }
  };

  return (
    <div className="ss-viewport" data-testid="space-supremacy-app">
      <div className="ss-atmosphere" />
      <div className="ss-grid" />
      <SpaceHomeBtn onClick={onBack} testid="base-staff-back" />

      <div className="hr-wrap" data-testid="base-staff">
        <div className="hr-head">
          <span className="hr-eyebrow">Mist Play · Sanctuary Operations</span>
          <h1>Base Staff</h1>
          <p className="bs-slotline" data-testid="base-staff-slots">
            Active staff <b>{assigned.length}</b> / {slots} slots
            <span className="bs-slothint"> · unlock more by upgrading the Command Centre</span>
          </p>
          {err ? <p className="bs-err" data-testid="base-staff-error">{err}</p> : null}
        </div>

        {workers === null ? (
          <div className="bg-loading" data-testid="base-staff-loading"><Loader2 size={26} className="bg-spin" /><span>Rousing the crew…</span></div>
        ) : (
          <div className="bs-list" data-testid="base-staff-list">
            {workers.map((w) => {
              const on = assigned.includes(w.worker_id);
              const full = !on && assigned.length >= slots;
              return (
                <div key={w.worker_id} className={`bs-card ${on ? "bs-card-on" : ""}`} data-testid={`worker-card-${w.worker_id}`} style={{ "--acc": w.accent }}>
                  <div className="bs-portrait">
                    {img(w.image_path)
                      ? <img src={img(w.image_path)} alt={w.name} data-testid={`worker-portrait-${w.worker_id}`} />
                      : <div className="hr-noimg">NO SIGNAL</div>}
                    {on ? <span className="bs-active-badge" data-testid={`worker-active-${w.worker_id}`}><Check size={11} /> ACTIVE</span> : null}
                  </div>
                  <div className="bs-body">
                    <span className="bs-name" data-testid={`worker-name-${w.worker_id}`}>{w.name}</span>
                    <span className="bs-role">{w.role}</span>
                    <span className="bs-zone"><MapPin size={10} /> {w.zone}</span>
                    <div className="bs-buff" data-testid={`worker-buff-${w.worker_id}`}>
                      <span className="bs-buff-name"><Sparkles size={11} /> {w.buff.name}</span>
                      <p className="bs-buff-desc">{w.buff.description}</p>
                      <span className={`bs-effect ${on ? "bs-effect-on" : ""}`} data-testid={`worker-effect-${w.worker_id}`}>
                        {effectLabel(w.effect)}{on ? " · LIVE" : ""}
                      </span>
                    </div>
                    <button
                      className={`bs-assign ${on ? "bs-assign-on" : ""}`}
                      disabled={busy === w.worker_id || full}
                      onClick={() => toggle(w)}
                      data-testid={`worker-assign-${w.worker_id}`}>
                      {busy === w.worker_id ? <Loader2 size={13} className="bg-spin" />
                        : on ? <><Check size={13} /> Assigned — tap to release</>
                          : full ? "No free slots" : <><Plus size={13} /> Assign to base</>}
                    </button>
                    <p className="bs-quote" data-testid={`worker-quote-${w.worker_id}`}>
                      <MessageSquareQuote size={12} /> {w.dialogue}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
